/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import "react-phone-input-2/lib/bootstrap.css";
import { toast } from "react-toastify";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { EmailAPI, ICreateEmail } from "../../../api/Email";
import { SentToContactEmailAtom } from "../../../atoms/ActiveEmailAtom";
import { InternalUserAtom, UserAtom } from "../../../atoms/UserAtom";
import { useDefaultAlias } from "../../../hook/useDefaultAlias";
import { getBody } from "../../../pages/new-email";
import { IContact } from "../../../types/Contact";
import EmailEditor, { EditorHandle } from "../../EmailEditor";
import BottomBar from "../../NewEmail/BottomBar";
import StyledAutocompleteSelect from "../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../StyledButton";
import { useModal } from "../Modal";

const ModalBody = styled.div`
  max-width: 1000px;
  min-width: 1000px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid #eceeee;

  margin: 20px -22px -25px;

  @media screen and (max-width: 1024px) {
    max-width: 100%;
    min-width: 100%;
    width: 100vw;
    height: 100%;

    margin: 0;
  }
`;

const ImgWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  grid-gap: 24px;
  position: absolute;
  transform: translate(0, 150%);
  > .description {
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #979797;
    text-align: center;
  }
  @media screen and (max-height: 640px) {
    transform: translate(0, 75%);
  }
`;

const FromWrapper = styled.div`
  width: 100%;

  padding: 12.5px 25px;

  display: flex;
  align-items: center;
  justify-content: start;

  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #333333;

  .from {
    font-weight: 600;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 640px;

  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;

  overflow-y: auto;

  @media screen and (max-height: 800px) {
    height: 480px;
  }

  @media screen and (max-height: 640px) {
    height: 320px;
  }

  @media screen and (max-width: 1024px) {
    height: 100%;
  }
`;

const Footer = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid #eceeee;

  padding: 10px;

  .wrapper {
    width: fit-content;

    display: flex;
    align-items: center;
    justify-content: center;

    > div {
      width: fit-content;
    }
  }

  .flex-wrapper {
    margin-left: 16px;

    display: flex;
    align-items: center;
    justify-content: center;
    grid-gap: 8px;

    .label {
      color: #555555;

      flex-shrink: 0;
    }
  }

  .buttons-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-gap: 16px;

    button {
      width: 80px;
    }
  }
`;

interface Props {
  contact: IContact;
}

export default function SendEmailToContact(props: Props): JSX.Element {
  const modal = useModal();

  const [isLoading, setIsLoading] = useState(false);
  const editorHandlerRef = useRef<EditorHandle>(null);
  const editor = editorHandlerRef.current?.getEditor();
  const user = useRecoilValue(UserAtom);
  const internalUser = useRecoilValue(InternalUserAtom);
  const sendToContactEmail = useRecoilValue(SentToContactEmailAtom);
  const resetSendToContactEmail = useResetRecoilState(SentToContactEmailAtom);

  const { defaultAlias } = useDefaultAlias({
    user,
    internalUser,
  });

  const options = user.emailOptions.footers.map((f) => ({
    value: f.id as string,
    name: f.name as string,
  }));

  const [selectedFooter, setSelectedFooter] = useState(options[0]);

  useEffect(() => {
    return () => {
      resetSendToContactEmail();
    };
  }, []);

  async function handleSendEmail() {
    try {
      setIsLoading(true);
      const { subject } = sendToContactEmail;

      if (!editor) return null;
      if (editor.isEmpty || subject === "") {
        toast("You need a subject and body", { type: "info" });
        return;
      }

      let selectedAliasId = defaultAlias.id;

      let body = getBody(editor, user);

      let newEmailSend: ICreateEmail = {
        ...sendToContactEmail,
        body,
      };

      const response = await EmailAPI.createEmail(
        {
          ...newEmailSend,
          footerId: selectedFooter.value,
          aliasId: selectedAliasId,
          defaultModeColorLink: user.emailOptions.defaultModeColorLink,
          darkModeColorLink: user.emailOptions.darkModeColorLink,
          labels: [],
          toList: [props.contact.email],
          isPersonal: true,
          showOnArchive: false,
        },
        "sent"
      );

      if (response) {
        toast("Your email was sent succesfully", {
          type: "success",
        });
        modal.popAll();
      }
    } catch (error) {
      toast("Your email could not be sent", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <ModalBody>
      <Wrapper>
        <FromWrapper>
          <div className="from">From </div>
          {`${defaultAlias.fromName} <${defaultAlias.address}>`}
        </FromWrapper>
        <EmailEditor ref={editorHandlerRef} mode="send-to-contact" />
        {editor?.isEmpty && (
          <ImgWrapper>
            <img src="/send-to-contacts.svg" alt="Send email to contacts" />
            <div className="description">
              One-off emails are meant for quick notes and will be sent without
              formatting.
              <br />
              Use a broadcast or campaign if you wish to use rich formatting
              capabilities.
            </div>
          </ImgWrapper>
        )}
      </Wrapper>

      <Footer>
        <div className="wrapper">
          <div className="flex-wrapper">
            <div className="label">Footer:</div>
            <StyledAutocompleteSelect
              selectedOption={selectedFooter}
              onOptionSelect={(option) => setSelectedFooter(option)}
              placeholder="Select a Footer"
              options={options}
            />
          </div>
        </div>

        <span className="buttons-wrapper">
          <StyledButton inverted onClick={() => modal.popModal()}>
            Cancel
          </StyledButton>
          <StyledButton onClick={handleSendEmail} isLoading={isLoading}>
            Send
          </StyledButton>
        </span>
      </Footer>
    </ModalBody>
  );
}
